/**
 * The one place that talks to the server.
 *
 * Everything goes through `request`, which does three things no caller should have to
 * remember: it sends the session cookie, it attaches the CSRF token on writes, and it
 * turns a failure into a thrown `ApiError` carrying the server's own message.
 *
 * That last point is the important one. A fetch wrapper that returns `[]` on failure
 * makes every screen in the product capable of saying "no alerts" when what happened
 * was "the database is down" — the single most expensive lie a security console can
 * tell.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Whether signing in again is the answer. */
  get isAuthProblem(): boolean {
    return this.status === 401 || this.status === 403
  }
}

function cookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`))
  return match?.[2] ? decodeURIComponent(match[2]) : null
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = { Accept: 'application/json' }

  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (UNSAFE.has(method)) {
    const token = cookie('csrftoken')
    // Missing rather than empty: sending `X-CSRFToken: ""` looks like a token to a
    // reader of the code and like nothing to the server.
    if (token) headers['X-CSRFToken'] = token
  }

  const response = await fetch(`/api/v1${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  })

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const payload: unknown = text ? safeParse(text) : null

  if (!response.ok) {
    throw new ApiError(response.status, messageFrom(payload, response.status), payload)
  }
  return payload as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function messageFrom(payload: unknown, status: number): string {
  if (typeof payload === 'string' && payload) return payload
  if (payload && typeof payload === 'object') {
    const detail = (payload as Record<string, unknown>).detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) return detail.join('; ')
    // DRF field errors: {"reason": ["This field is required."]}
    const fields = Object.entries(payload as Record<string, unknown>)
      .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
      .join('; ')
    if (fields) return fields
  }
  return `request failed (${status})`
}

/** Fetch the CSRF cookie. Called once, before the first write of a session. */
export async function primeCsrf(): Promise<void> {
  await request('/auth/csrf/')
}
